/**
 * Soft≠Live Page108 + Page156 — Live μDock remux ledger residual (Page42 buses retained).
 * Opaque bridge pull only · Soft video players stay deleted · liquid high-speed desk.
 * Parent posture: udock status bus (Page156) in Edge Sessions.
 */
import { useEffect, useState, useRef } from "react";
import { html, Alert, KV } from "./ui.js";
import { pullLiveBridge } from "./udock-api.js";

function chunkMeta(b64, kind) {
  let bytes = 0;
  try {
    // approx length without inventing media content
    const s = String(b64 || "");
    bytes = Math.floor((s.length * 3) / 4) - (s.endsWith("==") ? 2 : s.endsWith("=") ? 1 : 0);
  } catch {
    bytes = 0;
  }
  return { kind: kind || "unknown", bytes: Math.max(0, bytes) };
}

/**
 * @param {{ deviceId: string, sessionId: string, toast?: (m:string,t?:string)=>void, auto?: boolean }} props
 */
export function RemuxPullDesk({ deviceId, sessionId, toast, auto = true }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [poll, setPoll] = useState(!!auto);
  const [last, setLast] = useState(null);
  const [log, setLog] = useState([]);
  const tick = useRef(0);

  const pull = async (silent) => {
    if (!deviceId || !sessionId) return;
    setBusy(true);
    const out = await pullLiveBridge(deviceId, sessionId, 8);
    setBusy(false);
    if (!out.ok) {
      setErr(out.error || "Bridge pull refused");
      if (!silent) toast && toast(out.error || "Bridge pull refused", "bad");
      return;
    }
    setErr("");
    const data = out.data || {};
    const kinds = data.kinds || [];
    const chunks = data.chunks_b64 || [];
    const rows = chunks.map((b64, i) => chunkMeta(b64, kinds[i]));
    const pack = {
      count: Number(data.count || rows.length || 0),
      honesty: data.honesty || "opaque remux — no decode on this desk",
      live: data.live !== false,
      soft_neq_live: !!data.soft_neq_live,
      backend: data.backend || "",
      kinds: kinds.slice(),
      rows,
      ts: Date.now(),
    };
    setLast(pack);
    if (pack.count > 0) {
      tick.current += 1;
      setLog((prev) =>
        [
          {
            id: tick.current,
            count: pack.count,
            kinds: kinds.slice(0, 6).join(", ") || "—",
            bytes: rows.reduce((a, r) => a + r.bytes, 0),
          },
          ...prev,
        ].slice(0, 12),
      );
    }
    if (!silent && pack.count === 0) {
      toast && toast("No stream bytes yet — waiting for the unit", "warn");
    } else if (!silent && pack.count > 0) {
      toast && toast(`Received ${pack.count} opaque chunk(s)`, "go");
    }
  };

  useEffect(() => {
    if (!deviceId || !sessionId) return undefined;
    pull(true);
  }, [deviceId, sessionId]);

  useEffect(() => {
    if (!poll || !deviceId || !sessionId) return undefined;
    const id = setInterval(() => pull(true), 2500);
    return () => clearInterval(id);
  }, [poll, deviceId, sessionId]);

  if (!deviceId || !sessionId) {
    return html`<div className="udock-remux udock-remux--liquid" data-cmc-page="page108-udock"
      data-cmc-udock="page42" data-cmc-residual="page156-udock">
      <${Alert} tone="warn" title="No Live session"
        say="Open a live session first. Stream bytes only pull after a real grant."/>
    </div>`;
  }

  return html`
    <section className="udock-remux udock-remux--liquid" data-cmc-page="page108-udock"
      data-cmc-udock="page42" data-cmc-residual="page156-udock"
      aria-label="Live remux ledger">
      <header className="udock-remux__hd">
        <div>
          <p className="udock-remux__eyebrow">Live remux session</p>
          <h4>Opaque bridge pull · Live</h4>
          <p className="udock-remux__lede">
            Opaque bridge bytes only — the console never invents a player.
          </p>
        </div>
        <span className="udock-remux__seal" data-has=${last && last.count > 0 ? "1" : "0"}>
          ${last ? (last.count > 0 ? last.count + " chunks" : "empty ring") : "—"}
        </span>
      </header>

      ${err ? html`<${Alert} tone="warn" title="Bridge pull refused" say=${err}/>` : null}
      ${last ? html`<${Alert} tone=${last.count > 0 ? "info" : "warn"}
        title=${last.count > 0 ? "Live remux bytes" : "Waiting for stream bytes"}
        say=${last.honesty}/>` : null}

      <div className="btn-row udock-remux__acts" style=${{ marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <button type="button" className="btn btn--sm btn--go" disabled=${busy}
          onClick=${() => pull(false)}>
          ${busy ? "Pulling…" : "Pull stream bytes"}</button>
        <button type="button" className=${"btn btn--sm " + (poll ? "btn--go" : "btn--ghost")}
          onClick=${() => {
            setPoll((v) => !v);
            toast && toast(poll ? "Auto refresh off" : "Auto refresh on", "go");
          }}>
          ${poll ? "Auto-poll on · Live" : "Auto-poll off · Live"}</button>
      </div>

      <${KV} k="Pull count" v=${last ? String(last.count) : "—"}/>
      <${KV} k="Kinds (honest labels)" v=${last && last.kinds && last.kinds.length
        ? last.kinds.join(" · ")
        : "—"}/>
      <${KV} k="Last rows" v=${last && last.rows && last.rows.length
        ? last.rows.map((r) => r.kind + " " + r.bytes + "B").join(" · ")
        : "no opaque payload yet · Live empty"}/>

      <div className="udock-remux__log" aria-label="Remux pull log">
        ${!log.length
          ? html`<p className="note">Waiting for stream bytes from the unit.</p>`
          : log.map((row) => html`
              <div key=${row.id} className="udock-remux__row">
                <b>#${row.id}</b>
                <em>${row.count} chunk(s)</em>
                <span>${row.bytes} B · ${row.kinds}</span>
              </div>`)}
      </div>
    </section>`;
}
